import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";

const nodeText = (node) => {
  if (!node) return "";
  if (node.type === "text") return node.value || "";
  if (node.type === "inlineCode") return node.value || "";
  if (Array.isArray(node.children)) return node.children.map(nodeText).join("");
  return "";
};

const repeat = (ch, n) => new Array(Math.max(0, n) + 1).join(ch);

export const markdownToPlainText = (markdown = "") => {
  let tree;
  try {
    tree = unified().use(remarkParse).use(remarkGfm).parse(String(markdown || ""));
  } catch {
    return String(markdown || "");
  }

  const lines = [];

  const pushLine = (line = "") => {
    const value = String(line);
    lines.push(value.replace(/[ \t]+$/g, ""));
  };

  const walk = (node, ctx = {}) => {
    if (!node) return;

    switch (node.type) {
      case "root":
        (node.children || []).forEach((c) => walk(c, ctx));
        return;

      case "heading": {
        const text = nodeText(node).trim();
        if (text) pushLine(text);
        pushLine("");
        return;
      }

      case "paragraph": {
        const text = nodeText(node).replace(/\s+\n\s+/g, "\n").trim();
        if (text) pushLine(text);
        pushLine("");
        return;
      }

      case "thematicBreak":
        pushLine(repeat("-", 24));
        pushLine("");
        return;

      case "blockquote": {
        const before = lines.length;
        (node.children || []).forEach((c) => walk(c, { ...ctx, inBlockquote: true }));
        // Prefix only the lines generated by this blockquote.
        for (let i = before; i < lines.length; i++) {
          if (lines[i]) lines[i] = `> ${lines[i]}`;
        }
        pushLine("");
        return;
      }

      case "code": {
        const code = String(node.value || "").replace(/\s+$/g, "");
        if (code) {
          code.split("\n").forEach((l) => pushLine(l));
          pushLine("");
        }
        return;
      }

      case "list": {
        const ordered = Boolean(node.ordered);
        let counter = Number(node.start || 1);
        (node.children || []).forEach((li) => {
          walk(li, {
            ...ctx,
            listOrdered: ordered,
            listNumber: ordered ? counter++ : null,
          });
        });
        pushLine("");
        return;
      }

      case "listItem": {
        const bullet = ctx.listOrdered ? `${ctx.listNumber}. ` : "- ";
        const text = nodeText(node).replace(/\s+\n\s+/g, "\n").trim();
        if (text) pushLine(`${bullet}${text}`);
        return;
      }

      default:
        if (Array.isArray(node.children)) (node.children || []).forEach((c) => walk(c, ctx));
    }
  };

  walk(tree, {});

  return lines
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

